import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { COLOR_TOKENS } from '@backrs_ui/common-ui/src/designSystem/colors/colors';
import type { College } from '@backrs_ui/common-ui/src/models/college';
import { formatCurrency } from '@backrs_ui/common-ui/src/utils/stringFormatters.util';

import { Box, SxProps, Theme, Typography } from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Line } from 'react-chartjs-2';

import { ROILineChartHeader } from './ROILineChartHeader';

const ROI_LINE_CHART_STRINGS = appStrings.modules.charts.returnOnInvestment;

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  annotationPlugin,
);

// Middle half (25th-75th percentile) of four-year colleges at each horizon
const SHADED_BOUNDS = {
  upper: [28000, 371000, 717000, 1418000, 2120000],
  lower: [-45000, 224000, 489000, 1013000, 1537000],
};

const Y_AXIS_MIN = -400000;
const Y_AXIS_MAX = 5000000;

type ROIData = Pick<
  NonNullable<College['scorecardRoi']>,
  'roi_10_year' | 'roi_15_year' | 'roi_20_year' | 'roi_30_year' | 'roi_40_year'
>;

type ROILineChartProps = Readonly<{
  roiData?: ROIData | null;
  title: string;
}>;

export const ROILineChart = ({ roiData, title }: ROILineChartProps) => {
  if (roiData?.roi_15_year == null) return null;

  const dataPoints = [
    { year: 10, value: roiData.roi_10_year ?? null },
    { year: 15, value: roiData.roi_15_year ?? null },
    { year: 20, value: roiData.roi_20_year ?? null },
    { year: 30, value: roiData.roi_30_year ?? null },
    { year: 40, value: roiData.roi_40_year ?? null },
  ];

  const labels = dataPoints.map((p) => `${p.year} Years`);
  const values = dataPoints.map((p) => p.value);

  const data = {
    labels,
    datasets: [
      {
        label: 'upper',
        data: SHADED_BOUNDS.upper,
        borderColor: 'transparent',
        backgroundColor: 'transparent',
        pointRadius: 0,
        pointHoverRadius: 0,
        tension: 0,
        fill: false,
        order: 2,
      },
      {
        label: 'lower',
        data: SHADED_BOUNDS.lower,
        borderColor: 'transparent',
        backgroundColor: COLOR_TOKENS['Brand/100'],
        pointRadius: 0,
        pointHoverRadius: 0,
        tension: 0,
        fill: '-1',
        order: 2,
      },
      {
        label: title,
        data: values,
        borderColor: COLOR_TOKENS['Brand/500'],
        tension: 0,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: COLOR_TOKENS['Brand/500'],
        fill: false,
        order: 0,
      },
    ],
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        title: {
          display: true,
          text: ROI_LINE_CHART_STRINGS.yAxisLabel,
          color: COLOR_TOKENS['Colors/Text/text-tertiary'],
          font: {
            size: 12,
            family: 'Sarabun, sans-serif',
          },
        },
        grid: {
          tickColor: 'transparent',
          display: true,
          color: (context) => {
            const value = context.tick.value;

            if (value === 0) {
              return COLOR_TOKENS['Gray/300'];
            }

            return COLOR_TOKENS['Colors/Border/border-tertiary'];
          },
        },
        border: {
          display: false,
        },
        min: Y_AXIS_MIN,
        max: Y_AXIS_MAX,
        afterBuildTicks: (axis) => {
          axis.ticks = [Y_AXIS_MIN, 0, 500000, 1000000, 2000000, 3000000, 4000000, Y_AXIS_MAX].map(
            (value) => ({
              value,
            }),
          );
        },
        ticks: {
          callback: (value) => {
            const valueAsNumber = Number(value);

            if (valueAsNumber === Y_AXIS_MIN) return '';
            if (valueAsNumber === 0) return '$0 ';

            const absValue = Math.abs(valueAsNumber);
            const sign = valueAsNumber < 0 ? '-' : '';

            // format values <1M as $###k, others as $#M
            if (absValue < 1000000) {
              return `${sign}$${absValue / 1000}k `;
            }

            return `${sign}$${absValue / 1000000}M `;
          },
          font: {
            size: 12,
            weight: 'lighter',
            family: 'Sarabun, sans-serif',
          },
        },
      },
      x: {
        title: {
          display: false,
        },
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
        ticks: {
          align: 'center',
          font: {
            size: 12,
            weight: 'lighter',
            family: 'Sarabun, sans-serif',
          },
        },
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
    plugins: {
      tooltip: {
        enabled: true,
        displayColors: false,
        padding: {
          top: 8,
          bottom: 8,
          left: 12,
          right: 12,
        },
        caretSize: 0,
        yAlign: 'bottom',
        filter: function (tooltipItem) {
          return tooltipItem.dataset.label !== 'upper' && tooltipItem.dataset.label !== 'lower';
        },
        callbacks: {
          title: function (context) {
            return context[0]?.label ?? '';
          },
          label: function (context) {
            const value = context.parsed.y;

            return `ROI: ${formatCurrency(value)}`;
          },
        },
      },
      legend: {
        labels: {
          generateLabels: () => [],
        },
      },
      title: {
        display: false,
      },
    },
    hover: {
      mode: 'index',
      intersect: false,
    },
  };

  return (
    <Box sx={containerSx}>
      <ROILineChartHeader total_roi={roiData.roi_15_year} />

      <Box sx={chartContainerSx}>
        <Line data={data} options={options} />
      </Box>

      <Box sx={legendContainerSx}>
        <Box sx={legendColorBoxSx} />
        <Typography variant="text-sm-regular" sx={legendTextSx}>
          {ROI_LINE_CHART_STRINGS.legendLabel}
        </Typography>
      </Box>
    </Box>
  );
};

const chartContainerSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  height: '100%',
  mt: backrs.spacing['spacing-xl'],
});

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  '@media (max-width: 768px)': {
    width: '100%',
  },
  '@media (max-width: 400px)': {
    width: '100%',
  },
  height: { xs: 'auto', lg: '405px' },
  backgroundColor: backrs.colors['Colors/Base/White'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-primary']}`,
  boxShadow: backrs.shadows['shadow-xs'],
  borderRadius: backrs.radius['radius-xl'],
  padding: {
    xs: backrs.spacing['spacing-xl'],
    sm: backrs.spacing['spacing-2xl'],
  },
});

const legendContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-sm'],
  mt: backrs.spacing['spacing-lg'],
});

const legendColorBoxSx: SxProps<Theme> = ({ backrs }) => ({
  width: '16px',
  height: '16px',
  backgroundColor: COLOR_TOKENS['Brand/100'],
  borderRadius: backrs.radius['radius-xs'],
  flexShrink: 0,
});

const legendTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});
